/**
 * Authentication Hook
 * Provides authentication state and logout handling
 * Note: Global auth management is handled by AuthInitializer at app root level
 */

import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { authManager } from '@auth'
import { useMembershipRedux } from './useRedux'

/**
 * Hook to access authentication state and methods
 * Also sets up navigation callback for auth manager
 * @returns {Object} authentication state and methods
 */
export const useAuth = () => {
    const navigate = useNavigate()
    const membershipRedux = useMembershipRedux()

    // Set navigation callback for auth manager
    useEffect(() => {
        authManager.setNavigationCallback(navigate)

        return () => {
            authManager.setNavigationCallback(null)
        }
    }, [navigate])

    const actions = useMemo(
        () => ({
            logout: () => authManager.logout(),
            userLogout: () => authManager.userLogout(),
            isTokenValid: () => authManager.isTokenValid(),
            isTokenValidWithLogout: () => authManager.isTokenValidWithLogout(),
        }),
        []
    )

    return {
        isAuthenticated: membershipRedux.isAuthenticated,
        isLoading: membershipRedux.isLoading,
        error: membershipRedux.error,
        memberData: membershipRedux.memberData,
        ...actions,
    }
}

export default useAuth
